import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Box} from '@mui/material'
import DataTable, {PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import SearchBar from 'src/components-new/inputs/SearchBar'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {buildColumns, MedicalCenterRow} from './medicalCentersColumns'
import MedicalCenterDetailDialogNew from './MedicalCenterDetailDialogNew'

// Debounce delay for the name SearchBar, matching ParticipantLookupDialogNew's own
// server-backed search — long enough to not spam getMedicalCenters/-Count on every
// keystroke, short enough to still feel responsive.
const SEARCH_DEBOUNCE_MS = 300

/**
 * New-style equivalent of src/pages/medicalCenters/MedicalCenters.tsx. No page title/
 * breadcrumb is rendered here — HeaderBar derives it from the "medicalCenters" nav item
 * label. Pagination stays server-side (page/size passed straight through to the backend),
 * matching the legacy page's behavior and apiClient.getMedicalCenters' pagination contract.
 */
export default function MedicalCentersListNew(): JSX.Element {
  const configuration = useAppSelector(state => state.pspConfigReducer.data.medicalCentersConfigurations)

  const [rows, setRows] = useState<MedicalCenterRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  // The Filtra drawer's own filters (type/regione/provincia/città/medico) and the name
  // SearchBar are two independent sources kept as separate state — merging them into one
  // array would mean either side's "reset to []" (Azzera, or an empty search box) wipes
  // out the other's current value instead of just its own.
  const [advancedFilters, setAdvancedFilters] = useState<FilterResult[]>([])
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10})
  const [selectedMedicalCenter, setSelectedMedicalCenter] = useState<MedicalCenterRow>()
  const [detailOpen, setDetailOpen] = useState(false)
  // Only apiClientRequest's own PENDING/FULFILLED/REJECTED bookkeeping reads this — the
  // region/provincia/città cascade's loading/error state is handled inside Filters itself,
  // so nothing here needs to react to it.
  const [, setGeoStatus] = useState<string>(STATUS.PENDING)

  const searchDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current)
    }
  }, [])

  // Same filter fields as the legacy page (src/pages/medicalCenters/MedicalCenters.tsx),
  // minus `name` — that field now lives in the SearchBar above the table instead of inside
  // the Filtra drawer, as the most common/primary search. Used as a fallback when the PSP
  // doesn't configure `medicalCentersConfigurations.filters`. Regione/provincia/città are
  // wired to the real geo endpoints (rather than the old static ITALIAN_REGIONS/PROVINCE/
  // CITY lists) so picking one scopes the next: provincia only loads once a regione is
  // picked, città only once a provincia is picked — see Filters' dependsOn/loadOptions.
  const DEFAULT_FILTER_CONFIG: FilterItem[] = useMemo(() => [
    {
      id: 'type',
      label: 'Tipi centro medico',
      type: 'multiSelect',
      options: [
        {value: 'HOSPITAL', label: 'Ospedale'},
        {value: 'ASL', label: 'ASL'},
        {value: 'OTHER', label: 'Altro'},
      ],
    },
    {
      id: 'region',
      label: 'Regione',
      type: 'select',
      showSearch: true,
      loadOptions: () => apiClient.getRegions('', setGeoStatus),
    },
    {
      id: 'province',
      label: 'Provincia',
      type: 'select',
      dependsOn: 'region',
      showSearch: true,
      loadOptions: (values) => apiClient.getProvinces({region: values.region}, setGeoStatus),
    },
    {
      id: 'city',
      label: 'Città',
      type: 'select',
      dependsOn: 'province',
      showSearch: true,
      loadOptions: (values) => apiClient.getCities({province: values.province}, setGeoStatus),
    },
    {id: 'doctor', label: 'Medico', type: 'doctorLookup'},
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [])

  const filters = useMemo<FilterResult[]>(() => [
    ...advancedFilters,
    ...(debouncedSearch.trim() ? [{property: 'name', type: 'string', value: debouncedSearch.trim()}] : []),
  ], [advancedFilters, debouncedSearch])

  const fetchData = useCallback(() => {
    Promise.all([
      apiClient.getMedicalCenters(filters, setStatus, pagination),
      apiClient.getMedicalCentersCount(filters, setStatusCount),
    ])
      .then(([data, countResult]) => {
        if (Array.isArray(data)) setRows(data)
        const n = Number(countResult)
        if (!isNaN(n)) setCount(n)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [filters, pagination])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleFiltersSubmit = (values: FilterResult[]) => {
    setAdvancedFilters(values)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current)
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value)
      setPagination(prev => ({...prev, page: 0}))
    }, SEARCH_DEBOUNCE_MS)
  }

  const handleRowClick = (row: MedicalCenterRow) => {
    setSelectedMedicalCenter(row)
    setDetailOpen(true)
  }

  // Drop any PSP-configured `name` filter too — the SearchBar above owns that field now,
  // so it's never rendered a second time inside the drawer.
  const filterConfig: FilterItem[] = (configuration?.filters ?? DEFAULT_FILTER_CONFIG)
    .filter((item: FilterItem) => item.id !== 'name')
  const columns = buildColumns(configuration?.columnOptions)

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap'}}>
        <SearchBar
          value={search}
          onChange={handleSearchChange}
          placeholder="Cerca per nome centro medico..."
          sx={{flex: '1 1 240px', maxWidth: 360}}
        />
        <Box sx={{ml: 'auto'}}>
          <Filters config={filterConfig} onSubmit={handleFiltersSubmit} />
        </Box>
      </Box>

      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel recuperare i centri medici.'}
        </InlineMessage>
      )}

      <DataTable<MedicalCenterRow>
        columns={columns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun centro medico trovato"
        onRowClick={handleRowClick}
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      {selectedMedicalCenter && (
        <MedicalCenterDetailDialogNew
          medicalCenter={selectedMedicalCenter}
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </Box>
  )
}
